// Bezahlte Bons und Rücknahmen – lesen, drucken, Ware gegen Bon zurücknehmen – und Wertgutscheine nachschlagen.
// Bons sind Belege (Belegart 'pos_receipt', Rücknahme 'pos_return'): Suche und Verknüpfungen laufen über die
// gemeinsame Belegsuche. Jeder Bon ist als DEMO gekennzeichnet und kein Fiskalbeleg (lib/labels.ts).
import { defineContract } from '@shared/contract'
import { defineDocumentContracts } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { RECEIPT_TYPE, RETURN_TYPE } from '../lib/labels'
import { zTillEcho, zTillProof } from '../lib/offline/types'
import { zLocationRef, zReason, zSaleQty } from './common'
import { zReceiptView } from './views'

const READ = 'pos.shift.read'

export const receipt = defineDocumentContracts({
  module: 'pos',
  entity: 'receipt',
  documentType: RECEIPT_TYPE,
  title: 'Kassenbon',
  titlePlural: 'Kassenbons',
  permissions: { read: READ, write: 'pos.sale.create', post: 'pos.sale.create', reverse: 'pos.return.create' },
  keywords: ['Bon', 'Kassenbon', 'Kassenbeleg', 'Barverkauf', 'Quittung'],
})

export const receiptReturn = defineDocumentContracts({
  module: 'pos',
  entity: 'return',
  documentType: RETURN_TYPE,
  title: 'Kassenrücknahme',
  titlePlural: 'Kassenrücknahmen',
  permissions: { read: READ, write: 'pos.return.create', post: 'pos.return.create' },
  derivesFrom: [RECEIPT_TYPE],
  keywords: ['Rücknahme an der Kasse', 'Retoure', 'Umtausch', 'Geld zurück'],
})

// Angemeldet werden nur die lesenden Verträge: Bons entstehen und ändern sich ausschließlich über die Kasse.
export const receiptGet = receipt.get
export const receiptList = receipt.list
export const receiptPrint = receipt.print
export const returnGet = receiptReturn.get
export const returnList = receiptReturn.list
export const returnPrint = receiptReturn.print

export const receiptDetail = defineContract({
  name: 'pos.receipt.detail',
  title: 'Kassenbon mit Zahlungen anzeigen',
  description: 'Ein Bon oder eine Rücknahme der Kasse mit Positionen, Summen je Steuersatz, Zahlarten und Demo-Kennzeichnung.',
  llm: {
    description:
      'Liefert EINEN Kassenbon oder eine Kassenrücknahme – über die Kennung oder die Bonnummer (z. B. ' +
      '„K01-2026-000412“): Kasse, Zeit, Positionen mit Menge im Gebinde (Tausendstel) und Betrag brutto in Cent, ' +
      'Summen je Steuersatz, Zahlungen je Zahlart mit gegeben und Rückgeld, ' +
      'Alterskontrolle, ausgegebene Gutscheine, Rücknahmen zum Bon. Bons listet pos.receipt.list. Der Bon ist als ' +
      'DEMO gekennzeichnet und kein Fiskalbeleg.',
    keywords: ['Bon', 'Kassenbon', 'Bonnummer', 'Zahlart', 'Rückgeld'],
  },
  input: z
    .strictObject({ id: zId.optional(), number: z.string().trim().min(3).max(60).optional() })
    .refine((value) => Boolean(value.id) !== Boolean(value.number), { error: 'Bitte entweder die Kennung oder die Bonnummer angeben.' }),
  output: zReceiptView,
  permission: READ,
  risk: 'read',
  scope: 'record',
})

export const returnCreate = defineContract({
  name: 'pos.return.create',
  title: 'Ware gegen Bon zurücknehmen',
  description: 'Nimmt Positionen eines Bons zurück: Ware in den Bestand oder Sperrbestand, Erstattung in der ursprünglichen Zahlart.',
  llm: { description: 'Nimmt Positionen eines bezahlten Bons an einer Kasse mit offener Schicht zurück. Nur an der Kasse selbst – Ware und Geld wechseln dabei die Hand.', keywords: ['Rücknahme', 'Retoure', 'Geld zurück'] },
  input: z.strictObject({
    receiptId: zId,
    /** Kasse, an der zurückgenommen wird – am Standort des Bons, mit offener Schicht */
    registerId: zId,
    reason: zReason,
    lines: z
      .array(
        z.strictObject({
          lineId: zId,
          returnQty: zSaleQty,
          /** stock = zurück in den Bestand · blocked = in den Sperrbestand */
          disposition: z.enum(['stock', 'blocked']).default('stock'),
        }),
      )
      .min(1, { error: 'Bitte wählen Sie mindestens eine Position.' })
      .max(100),
    /** Pflicht an Offline-Kassen, mit nextBonId (docs/kasse-offline/SPEZIFIKATION.md §14.2) */
    till: zTillProof.optional(),
  }),
  output: z.object({
    receipt: zReceiptView,
    refundCents: z.int(),
    refunds: z.array(z.object({ method: z.string(), label: z.string(), amountCents: z.int() })),
    message: z.string(),
    till: zTillEcho.optional(),
  }),
  permission: 'pos.return.create',
  risk: 'financial',
  scope: 'record',
  assistant: 'never',
})

export const voucherGet = defineContract({
  name: 'pos.voucher.get',
  title: 'Wertgutschein prüfen',
  description: 'Guthaben und Bewegungen eines Wertgutscheins.',
  llm: {
    description:
      'Liefert zu einem Gutschein-Code das Guthaben in Cent, den Ausgabebetrag, wo und wann er ausgegeben wurde ' +
      'und seine Einlösungen. Verwenden für „was ist auf dem Gutschein noch drauf“. Andere Bons sind keine ' +
      'Wertgutscheine und stehen hier nicht.',
    keywords: ['Gutschein', 'Wertgutschein', 'Guthaben', 'Gutschein prüfen'],
  },
  input: z.strictObject({ code: z.string({ error: 'Bitte den Gutschein-Code eingeben.' }).trim().min(4, { error: 'Bitte den Gutschein-Code eingeben.' }).max(40) }),
  output: z.object({
    id: zId,
    code: z.string(),
    initialCents: z.int(),
    balanceCents: z.int(),
    issuedAt: z.date(),
    issuedLocation: zLocationRef,
    issuedReceipt: z.object({ id: zId, number: z.string().nullable() }).nullable(),
    entries: z.array(z.object({ at: z.date(), amountCents: z.int(), receipt: z.object({ id: zId, number: z.string().nullable() }).nullable() })),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})
